import { useMemo } from 'react';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useQuery } from '@tanstack/react-query';
import { formatPlainDate } from '@airreading/common';
import { formatReassignmentReason, StudentGroupReassignmentRecord } from '@airreading/common/data';
import { EntityTable } from '@/components/table/entity-table.js';
import { InfoCard } from '@/components/info-card.js';
import { Badge } from '@/components/ui/badge.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { EmptyArray, getStudentGroupReassignmentRequests } from '@/lib/api-calls.js';
import { ReassignmentRequestDisplayStatus } from '@/routes/_layout/student-groups/-components/reassignment-request-display-status.js';

const columnHelper = createColumnHelper<StudentGroupReassignmentRecord>();

type CoachLike = StudentGroupReassignmentRecord['originalCoach'];

const CoachBadge = ({ coach }: { coach: CoachLike }) => {
  const name = coach?.fullNameOrEmail ?? '-';

  if (name === '-') {
    return <span>-</span>;
  }

  return (
    <Badge variant="outline" className="bg-[#F1F1F1] font-normal">
      {name}
    </Badge>
  );
};

const PlainDateCell = ({ date }: { date: StudentGroupReassignmentRecord['requestPlainDate'] }) => {
  return <span>{formatPlainDate(date)}</span>;
};

const columns = [
  columnHelper.accessor('requestPlainDate', {
    id: 'requestDate',
    header: 'Request date',
    enableSorting: false,
    cell: ({ getValue }) => <PlainDateCell date={getValue()} />,
  }),
  columnHelper.accessor('effectivePlainDate', {
    id: 'effectiveDate',
    header: 'Effective date',
    enableSorting: false,
    cell: ({ getValue }) => <PlainDateCell date={getValue()} />,
  }),
  columnHelper.accessor('originalCoach', {
    id: 'previousTeacher',
    header: 'Previous teacher',
    enableSorting: false,
    cell: ({ getValue }) => <CoachBadge coach={getValue()} />,
  }),
  columnHelper.accessor('reassignedCoach', {
    id: 'newTeacher',
    header: 'New teacher',
    enableSorting: false,
    cell: ({ getValue }) => <CoachBadge coach={getValue()} />,
  }),
  columnHelper.accessor((row) => row, {
    id: 'reasons',
    header: 'Reassignment reason',
    enableSorting: false,
    cell: ({ getValue }) => <span>{formatReassignmentReason(getValue())}</span>,
  }),
  columnHelper.accessor('reassignmentNotes', {
    id: 'reassignmentNotes',
    header: 'Reassignment notes',
    enableSorting: false,
    cell: ({ getValue }) => <span className="whitespace-pre-wrap">{getValue()?.trim() || '-'}</span>,
  }),
  columnHelper.accessor('requester', {
    id: 'requester',
    header: 'Requester',
    enableSorting: false,
    cell: ({ getValue }) => <CoachBadge coach={getValue()} />,
  }),
  columnHelper.accessor('displayStatus', {
    id: 'reassignmentStatus',
    header: 'Reassignment status',
    enableSorting: false,
    cell: ({ getValue }) => <ReassignmentRequestDisplayStatus displayStatus={getValue()} />,
  }),
];

export const ReassignmentRecordsCard = ({ classId }: { classId: string }) => {
  const { data, error, refetch, isPending } = useQuery(getStudentGroupReassignmentRequests(classId));
  const reassignmentRequests = useMemo(() => data?.reassignmentRequests ?? EmptyArray, [data]);

  const table = useReactTable({
    data: reassignmentRequests,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <InfoCard headerText="Reassignment records">
      <EntityTable
        table={table}
        hasError={!!error}
        isLoading={isPending}
        errorState={<TableErrorState refetchAction={refetch} />}
        emptyState="No reassignment records found"
        emptyStateClassName="translate-y-0"
      />
    </InfoCard>
  );
};
